import React, { useCallback, useEffect, useState } from 'react';

import {
  MalwareScanStatus,
  TeacherApplication,
  TeacherApplicationState,
  TeacherDocument,
  approveApplication,
  getAdminApplicationDetail,
  getAdminDocumentLink,
  listAdminQueue,
  rejectApplication,
  requestResubmit,
  startAdminReview,
} from '../api/teacher-applications';
import {
  REVIEW_REASON_CODES,
  REVIEW_REASON_LABELS,
  ReviewReasonCode,
} from '../domain/review-reasons';
import {
  Drawer,
  Empty,
  Field,
  FilterBar,
  Note,
  OpsPage,
  Pill,
  PillTone,
  Row,
  Table,
  Value,
} from '../../../design-system/ops';
import AdminVerificationFacts from '../components/AdminVerificationFacts';

/**
 * Admin review of teacher applications — UC-A07's console surface.
 *
 * Rewritten onto the design system, and two things were fixed while it was
 * open.
 *
 * The internal note for a rejection or a resubmission was taken through
 * `window.prompt`, which cannot be styled and covers the documents the
 * reviewer is deciding about. Worse, asking which documents to reject was a
 * second prompt expecting **comma-separated document ids typed by hand** —
 * pre-filled with all of them, so the path of least resistance was to reject
 * every document the candidate had uploaded. Both are now in the drawer, and
 * the documents are checkboxes.
 */

const STATE_FILTERS: Array<{ value: TeacherApplicationState | 'all'; label: string }> = [
  { value: 'SUBMITTED', label: 'Enviadas' },
  { value: 'UNDER_REVIEW', label: 'En revisión' },
  { value: 'NEEDS_RESUBMIT', label: 'Reenvío pedido' },
  { value: 'APPROVED', label: 'Aprobadas' },
  { value: 'REJECTED', label: 'Rechazadas' },
  { value: 'all', label: 'Todas' },
];

const STATE_TONE: Record<string, PillTone> = {
  DRAFT: 'neu',
  SUBMITTED: 'tint',
  UNDER_REVIEW: 'warn',
  APPROVED: 'ok',
  REJECTED: 'bad',
  NEEDS_RESUBMIT: 'warn',
};

/**
 * The malware scanner's verdict, as the reviewer reads it (AD-A07-08). Only a
 * clean file can be opened; a threat is shown, never opened, and is grounds
 * for asking the candidate to upload the document again.
 */
const SCAN_PILL: Record<MalwareScanStatus, { label: string; tone: PillTone }> = {
  PENDING: { label: 'Analizando', tone: 'warn' },
  CLEAN: { label: 'Sin amenazas', tone: 'ok' },
  THREATS_FOUND: { label: 'Amenaza detectada', tone: 'bad' },
  UNSUPPORTED: { label: 'No analizable', tone: 'bad' },
  ACCESS_DENIED: { label: 'Error de análisis', tone: 'bad' },
  FAILED: { label: 'Error de análisis', tone: 'bad' },
};

/** What the reviewer is told when the server refuses to open a file, by verdict. */
const SCAN_REFUSAL: Record<MalwareScanStatus, string> = {
  PENDING:
    'El archivo aún se está analizando en busca de software malicioso. Inténtalo en unos minutos.',
  CLEAN: 'No se pudo abrir el documento.',
  THREATS_FOUND:
    'El análisis detectó una amenaza en este archivo. No se puede abrir; pide al candidato que lo suba de nuevo.',
  UNSUPPORTED:
    'El archivo no pudo analizarse y no se puede abrir. Pide al candidato que lo suba de nuevo.',
  ACCESS_DENIED:
    'El archivo no pudo analizarse y no se puede abrir. Pide al candidato que lo suba de nuevo.',
  FAILED:
    'El archivo no pudo analizarse y no se puede abrir. Pide al candidato que lo suba de nuevo.',
};

/** Verdicts that will not become clean by waiting. The file cannot be opened. */
const UNOPENABLE: ReadonlySet<MalwareScanStatus> = new Set<MalwareScanStatus>([
  'THREATS_FOUND',
  'UNSUPPORTED',
  'ACCESS_DENIED',
  'FAILED',
]);

const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleString('es-MX') : '—');

/** Minimum length the API enforces on an internal note. */
const MIN_REASON = 5;

const AdminTeacherApplications: React.FC = () => {
  const [filter, setFilter] = useState<TeacherApplicationState | 'all'>('SUBMITTED');
  const [queue, setQueue] = useState<TeacherApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<{
    application: TeacherApplication;
    documents: TeacherDocument[];
  } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionInfo, setActionInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [reasonCode, setReasonCode] = useState<ReviewReasonCode>('DOCUMENT_UNREADABLE');
  /** The internal note. The candidate never reads it — they see the reason code's text. */
  const [note, setNote] = useState('');
  const [rejectedDocs, setRejectedDocs] = useState<string[]>([]);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      setQueue((await listAdminQueue(filter === 'all' ? undefined : filter)).applications ?? []);
    } catch (e: any) {
      setActionError(e?.message || 'Error cargando la cola');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const openDetail = async (applicationId: string) => {
    setActionError(null);
    setActionInfo(null);
    setNote('');
    setRejectedDocs([]);
    try {
      setSelected(await getAdminApplicationDetail(applicationId));
    } catch (e: any) {
      setActionError(e?.message || 'Error cargando la solicitud');
    }
  };

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    if (!selected) return;
    setBusy(true);
    setActionError(null);
    setActionInfo(null);
    try {
      await fn();
      await openDetail(selected.application.applicationId);
      await loadQueue();
      setActionInfo(ok);
    } catch (e: any) {
      setActionError(e?.message || 'No se pudo completar la acción');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Open one document through a fresh, two-minute, audited link (AD-A07-08).
   *
   * The tab is opened inside the click, before the request, because a window
   * opened after an `await` is a popup the browser blocks. It is pointed at
   * the link once the server has issued one, and closed if it refuses.
   */
  const openDocument = async (doc: TeacherDocument) => {
    if (!selected) return;
    setActionError(null);
    setActionInfo(null);
    const tab = window.open('', '_blank');
    try {
      const { downloadUrl } = await getAdminDocumentLink(
        selected.application.applicationId,
        doc.teacherId,
        doc.documentId,
      );
      if (tab) {
        tab.opener = null;
        tab.location.href = downloadUrl;
      } else {
        setActionInfo(
          'El navegador bloqueó la pestaña. Permite ventanas emergentes e inténtalo de nuevo.',
        );
      }
    } catch (e: any) {
      tab?.close();
      const scan = e?.body?.scanStatus as MalwareScanStatus | undefined;
      const message = scan
        ? SCAN_REFUSAL[scan]
        : e?.status === 403
        ? 'No tienes autorización para abrir los documentos de esta solicitud.'
        : e?.message || 'No se pudo abrir el documento';
      // The refusal may have recorded a new scan verdict; reload to show it.
      // Before the message, because reloading clears the banner.
      await openDetail(selected.application.applicationId).catch(() => undefined);
      setActionError(message);
    }
  };

  const state = selected?.application.state;
  const reviewable = state === 'SUBMITTED' || state === 'UNDER_REVIEW';
  const noteTooShort = note.trim().length < MIN_REASON;

  return (
    <OpsPage
      title="Solicitudes de profesor"
      subtitle="Una persona espera al otro lado de cada una de estas. El motivo que elijas es lo único que verá."
    >
      <FilterBar
        label="Filtrar por estado"
        value={filter}
        onChange={setFilter}
        options={STATE_FILTERS}
      />

      {actionError ? <Note tone="bad">{actionError}</Note> : null}
      {actionInfo ? <Note tone="warn">{actionInfo}</Note> : null}

      {loading ? (
        <Note>Cargando…</Note>
      ) : queue.length === 0 ? (
        <Empty icon="🏫" title="Sin solicitudes" detail="Nada en este estado." />
      ) : (
        <Table columns="1fr 220px 170px 140px" head={['Profesor', 'Materias', 'Enviada', 'Estado']}>
          {queue.map((app) => (
            <Row
              key={app.applicationId}
              onOpen={() => openDetail(app.applicationId)}
              label={`Revisar la solicitud ${app.applicationId}`}
            >
              <span className="mut" style={{ fontFamily: 'ui-monospace, monospace' }}>
                {app.teacherId}
              </span>
              <span>{app.profile.subjectCoverage.join(', ')}</span>
              <span className="mut">{fmt(app.submittedAt)}</span>
              <Pill tone={STATE_TONE[app.state] ?? 'neu'}>{app.state}</Pill>
            </Row>
          ))}
        </Table>
      )}

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        title="Solicitud"
        meta={selected ? selected.application.applicationId : undefined}
        footer={
          selected && reviewable ? (
            <>
              {state === 'SUBMITTED' ? (
                <button
                  className="btn btn-o"
                  disabled={busy}
                  onClick={() =>
                    run(
                      () => startAdminReview(selected.application.applicationId),
                      'Revisión iniciada.',
                    )
                  }
                >
                  Iniciar revisión
                </button>
              ) : null}
              <button
                className="btn btn-o"
                disabled={busy || noteTooShort || rejectedDocs.length === 0}
                onClick={() =>
                  run(
                    () =>
                      requestResubmit(selected.application.applicationId, {
                        reasonCode,
                        reason: note.trim(),
                        rejectedDocumentIds: rejectedDocs,
                      }),
                    'Reenvío solicitado.',
                  )
                }
              >
                Pedir reenvío
              </button>
              <button
                className="btn btn-d"
                disabled={busy || noteTooShort}
                onClick={() =>
                  run(
                    () =>
                      rejectApplication(
                        selected.application.applicationId,
                        reasonCode,
                        note.trim(),
                      ),
                    'Solicitud rechazada.',
                  )
                }
              >
                Rechazar
              </button>
              <button
                className="btn btn-p"
                disabled={busy}
                onClick={() =>
                  run(
                    () => approveApplication(selected.application.applicationId),
                    'Solicitud aprobada.',
                  )
                }
              >
                Aprobar
              </button>
            </>
          ) : null
        }
      >
        {selected ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <Pill tone={STATE_TONE[selected.application.state] ?? 'neu'}>
                {selected.application.state}
              </Pill>
            </div>

            <Field label="Profesor">
              <Value>
                <code>{selected.application.teacherId}</code>
              </Value>
            </Field>

            {Object.entries(selected.application.profile).map(([key, value]) => (
              <Field key={key} label={key}>
                <Value>{Array.isArray(value) ? value.join(', ') : String(value)}</Value>
              </Field>
            ))}

            {/* UC-A07 ADM-07-FR-04/05/09/10 — liveness and background as facts. */}
            <AdminVerificationFacts teacherId={selected.application.teacherId} />

            <Field label={`Documentos (${selected.documents.length})`}>
              {selected.documents.length === 0 ? (
                <Value>Sin documentos.</Value>
              ) : (
                selected.documents.map((d) => (
                  <div
                    key={d.documentId}
                    style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 0' }}
                  >
                    <label style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1 }}>
                      {/* Checkboxes rather than a prompt asking for comma-separated
                          ids pre-filled with all of them — which made rejecting
                          every document the path of least resistance. */}
                      <input
                        type="checkbox"
                        checked={rejectedDocs.includes(d.documentId)}
                        disabled={!reviewable}
                        aria-label={`Marcar ${d.type} para reenvío`}
                        onChange={(e) =>
                          setRejectedDocs((prev) =>
                            e.target.checked
                              ? [...prev, d.documentId]
                              : prev.filter((id) => id !== d.documentId),
                          )
                        }
                      />
                      <span style={{ flex: 1 }}>
                        <b>{d.type}</b>
                        <br />
                        <span className="mut">Subido {fmt(d.uploadedAt)}</span>
                      </span>
                    </label>
                    {d.scanStatus ? (
                      <Pill tone={SCAN_PILL[d.scanStatus].tone}>
                        {SCAN_PILL[d.scanStatus].label}
                      </Pill>
                    ) : null}
                    <Pill tone={STATE_TONE[d.status] ?? 'neu'}>{d.status}</Pill>
                    {reviewable && d.status !== 'AWAITING_UPLOAD' ? (
                      <button
                        className="btn btn-o"
                        disabled={d.scanStatus !== undefined && UNOPENABLE.has(d.scanStatus)}
                        aria-label={`Abrir ${d.type}`}
                        onClick={() => openDocument(d)}
                      >
                        Abrir
                      </button>
                    ) : null}
                  </div>
                ))
              )}
            </Field>

            {reviewable ? (
              <>
                <Field label="Motivo que verá el candidato">
                  <select
                    id="review-reason-code"
                    className="inp"
                    value={reasonCode}
                    aria-label="Motivo que verá el candidato"
                    onChange={(e) => setReasonCode(e.target.value as ReviewReasonCode)}
                  >
                    {REVIEW_REASON_CODES.map((code) => (
                      <option key={code} value={code}>
                        {REVIEW_REASON_LABELS[code]}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Nota interna">
                  <textarea
                    id="review-internal-note"
                    className="inp"
                    rows={3}
                    value={note}
                    aria-label="Nota interna para el expediente"
                    placeholder="Para el expediente. El candidato no la lee."
                    onChange={(e) => setNote(e.target.value)}
                  />
                  {noteTooShort ? (
                    <Note>
                      Rechazar o pedir reenvío necesita una nota de al menos {MIN_REASON}{' '}
                      caracteres. Pedir reenvío necesita además al menos un documento marcado.
                    </Note>
                  ) : null}
                </Field>
              </>
            ) : null}
          </>
        ) : null}
      </Drawer>
    </OpsPage>
  );
};

export default AdminTeacherApplications;
